import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../../constants";
import SearchBar from "../common/SearchBar";
import VectorIcon from "../../assets/icons/vectorIcon.svg";
import MenuIcon from "../../assets/icons/menuIcon.svg";
import LocIcon from "../../assets/icons/locIcon.svg";
import TimeIcon from "../../assets/icons/timeicon.svg";
import BagIcon from "../../assets/icons/bagIcon.svg";
import PaperIcon from "../../assets/icons/paperIcon.svg";
import CalenderIcon from "../../assets/icons/calenderIcon.svg";
import EyeIcon from "../../assets/icons/eyeIcon.svg";
import JobFilter from "../JobFilter";
import Logo from "../../assets/logo.svg";


function CandidateJobs() {
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams(filters).toString();
        const url = `${jobsEndpoint}list/?${queryParams}`;
        const response = await axios.get(url);
        setJobs(response.data.results || response.data);
      } catch (err) {
        setError("Failed to load jobs.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [filters]);

  const handleCardClick = (job) => {
    navigate(`/candidate/job-details/${job}`);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">Loading jobs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* div heading */}
      <div className="flex sm:flex-row flex-col justify-between sm:items-center items-start gap-6 w-full ">
        <div>
          <h1 className="text-gray-800 font-semibold text-lg ">Jobs</h1>
          <p className="text-gray-400 font-normal text-sm">
            Here are all the jobs you can apply ({jobs.length} available)
          </p>
        </div>
        <div className="flex gap-3 items-center sm:justify-end w-full">
          <button
            className="px-4 py-3 cursor-pointer rounded-lg bg-[#19BFB7] text-white sm:text-sm text-xs font-semibold"
            onClick={() => navigate("/candidate/jobs/appliedjobs")}
          >
            view Applied Jobs
          </button>
        </div>
      </div>

      <JobFilter onApplyFilters={handleApplyFilters} />

      <div className="mt-16 flex flex-col gap-10">
        {/* div filter */}
        <div className=" flex justify-between items-center">
          <div className="flex gap-3 flex-row sm:flex-nowrap flex-wrap items-center">
          </div>
          <div className="sm:flex gap-5 items-center hidden">
            <button className="border border-gray-200 rounded-md p-1.5 cursor-pointer hover:bg-gray-100 duration-150 transition text-gray-700 ">
              <img src={VectorIcon} alt="" />
            </button>{" "}
            <button className="border border-gray-200 rounded-md p-1.5 cursor-pointer hover:bg-gray-100 duration-150 transition text-gray-700 ">
              <img src={MenuIcon} alt="" />
            </button>
          </div>
        </div>

        {/* div card */}
        {jobs.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500">No jobs found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid xl:grid-cols-3 sm:grid-cols-2 grid-cols-1  gap-5">
            {jobs.map((job) => (
              <div
                key={job.id}
                onClick={() => handleCardClick(job.id)}
                className="border border-gray-200 rounded-lg p-4 flex flex-col gap-3 cursor-pointer  shadow-sm hover:shadow-lg duration-150 transition"
              >
                <div className="flex items-center justify-between">
                  <div className="flex gap-2 items-center">
                    <img
                      src={Logo}
                      className="w-7 h-7 rounded-full"
                      alt="Company Logo"
                    />
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-2 items-baseline ">
                        <h2 className="text-gray-800 text-[16px] font-semibold ">
                          {job.title}
                        </h2>
                        <span className="border border-blue-300 bg-blue-100 text-blue-500 font-semibold px-3 py-1 rounded-xl text-[10px] flex gap-1 items-center">
                          <span> &bull; </span>
                          {job.department || job.company}
                        </span>
                      </div>
                      <div className="flex ">
                        <p className="text-gray-500 font-normal pr-2 text-xs border-r border-gray-500">
                          {job.company}
                        </p>
                        <p className="text-gray-500 font-normal text-xs  pl-2">
                          {job.location || "Remote"}
                        </p>
                      </div>
                    </div>
                  </div>
             
                </div>

                <div className="flex gap-3 items-center">
                  <span className="text-gray-500 font-semibold text-xs flex gap-1 items-center">
                    <img src={LocIcon} alt="loc icon" className="w-4 h-4" />
                    {job.employment_type || "Full-time"}
                  </span>
                  <span className="text-gray-500 font-semibold text-xs flex gap-1 items-center">
                    <img src={TimeIcon} alt="time icon" className="w-4 h-4" />
                    {job.schedule || "Day shift"}
                  </span>
                  <span className="text-gray-500 font-semibold text-xs flex gap-1 items-center">
                    <img src={BagIcon} alt="bag icon" className="w-4 h-4" />
                    {job.experience_level || "Mid-level"}
                  </span>
                </div>

                <div className="pb-5 border-b border-gray-200">
                  <p className="text-gray-500  font-normal text-xs leading-5">
                    {job.description 
                      ? (job.description.split(" ").slice(0, 25).join(" ") + "...")
                      : "Job description not available..."}
                  </p>
                </div>

                <div className="flex items-center gap-4 pt-1">
                  <span className="text-gray-500 font-semibold text-xs flex gap-1 items-center">
                    <img
                      src={CalenderIcon}
                      alt="calender icon"
                      className="w-4 h-4"
                    />
                    {job.date_updated 
                      ? new Date(job.date_updated).toLocaleDateString()
                      : "Recently"}
                  </span>
                  <span className="text-gray-500 font-semibold text-xs flex gap-1 items-center">
                    <img src={PaperIcon} alt="paper icon" className="w-4 h-4" />
                    {job.applications_count > 100 ? "100+" : (job.applications_count || "0")}
                  </span>
                  <span className="text-gray-500 font-semibold text-xs flex gap-1 items-center">
                    <img src={EyeIcon} alt="eye icon" className="w-4 h-4" />
                    {job.views_count || "0"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CandidateJobs;